import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePageAccess } from '../../hooks/usePageAccess';
import { supabase } from '../../lib/supabase';
import { getAdminId } from '../../utils/getAdminId';
import { Package, ChartBar as BarChart3, FileText, Settings, Users, Truck, ShoppingCart, Store, QrCode, LogOut, Chrome as Home, MapPin, ShoppingBag, ClipboardList, ClipboardCheck, ClipboardPen, Tag, TrendingUp, TriangleAlert as AlertTriangle, Building2, RotateCcw } from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const { hasAccess } = usePageAccess();
  const [userLogo, setUserLogo] = useState<string | null>(null);
  const [openIssueCount, setOpenIssueCount] = useState(0);

  useEffect(() => {
    const loadUserLogo = async () => {
      if (user?.id) {
        try {
          const { data: settings } = await supabase
            .from('user_settings')
            .select('avatar_data')
            .eq('user_id', user.id)
            .maybeSingle();

          if (settings?.avatar_data) {
            setUserLogo(settings.avatar_data);
          }
        } catch (error) {
          console.error('Failed to load user logo:', error);
        }
      }
    };

    loadUserLogo();
  }, [user?.id]);

  useEffect(() => {
    const loadIssueCount = async () => {
      const adminId = getAdminId(user);
      if (!adminId) return;

      try {
        const { data } = await supabase.rpc('get_open_order_issues_count', {
          p_admin_id: adminId,
        });
        setOpenIssueCount(typeof data === 'number' ? data : 0);
      } catch {}
    };

    loadIssueCount();
    const interval = setInterval(loadIssueCount, 60000);
    return () => clearInterval(interval);
  }, [user?.id, user?.role]);

  const handleLogout = () => {
    logout();
    onClose();
  };

  interface MenuItem {
    path: string;
    icon: React.FC<{ className?: string }>;
    label: string;
    pageKey?: string;
    badge?: number;
  }

  // Buyer users don't see the sidebar (B2B page has its own nav)
  const { buyer: b2bBuyer } = useAuth() as any;
  if (b2bBuyer && !user) {
    return null;
  }

  let menuItems: MenuItem[] = [];

  if (user?.role === 'super_admin') {
    menuItems = [
      { path: '/admin', icon: Users, label: t('nav.admin') }
    ];
  } else {
    const isAdmin = user?.role === 'admin';

    const allItems: MenuItem[] = [
      { path: '/dashboard',        icon: Home,           label: t('nav.dashboard') },
      { path: '/inventory',        icon: Package,        label: t('nav.inventory'),       pageKey: 'inventory' },
      { path: '/incoming',         icon: Truck,          label: t('nav.incoming'),        pageKey: 'incoming' },
      { path: '/outgoing',         icon: Truck,          label: t('nav.outgoing'),        pageKey: 'outgoing' },
      { path: '/package-tracing',  icon: MapPin,         label: 'Package Tracing',        pageKey: 'package-tracing' },
      { path: '/in-house-shipment', icon: Store,         label: 'In-house Shipment',      pageKey: 'in-house-shipment' },
      { path: '/return-shipment',  icon: RotateCcw,      label: 'Return Shipment',        pageKey: 'return-shipment' },
      { path: '/shipping-labels',  icon: Tag,            label: 'Shipping Labels',        pageKey: 'shipping-labels' },
      { path: '/orders-issues',    icon: AlertTriangle,  label: 'Orders Issue',           pageKey: 'orders-issues', badge: openIssueCount },
      { path: '/barcode-generator', icon: QrCode,        label: t('nav.barcode'),         pageKey: 'barcode-generator' },
      { path: '/reports',          icon: BarChart3,      label: t('nav.reports'),          pageKey: 'reports' },
      { path: '/worker-terminal',  icon: ClipboardList,  label: 'Worker Terminal',        pageKey: 'worker-terminal' },
      { path: '/reservation-terminal', icon: ClipboardCheck, label: 'Reservation Terminal', pageKey: 'reservation-terminal' },
      { path: '/shop-terminal',    icon: Store,          label: 'Shop Terminal',          pageKey: 'shop-terminal' },
      { path: '/shop-stock-count', icon: ClipboardPen,   label: 'Shop Stock Count',       pageKey: 'shop-stock-count' },
      { path: '/shopify',          icon: ShoppingBag,    label: 'Shopify',                pageKey: 'shopify' },
      { path: '/shopify/orders',   icon: ShoppingCart,   label: 'Shopify Orders',         pageKey: 'shopify-orders' },
      { path: '/sales-history',    icon: TrendingUp,     label: 'Sales History',          pageKey: 'sales-history' },
      { path: '/analytics',        icon: TrendingUp,     label: 'Analytics',              pageKey: 'analytics' },
      { path: '/sales-invoices',   icon: FileText,       label: 'Sales Invoices',         pageKey: 'sales-invoices' },
      { path: '/devices',          icon: Settings,       label: 'Device Manager',         pageKey: 'devices' },
      { path: '/settings',         icon: Settings,       label: t('nav.settings') },
    ];

    if (isAdmin) {
      allItems.push({ path: '/b2b', icon: Building2, label: 'B2B Portal' });
      allItems.push({ path: '/admin/users', icon: Users, label: t('nav.users') });
    } else {
      allItems.push({ path: '/b2b', icon: Building2, label: 'B2B Portal', pageKey: 'b2b' });
    }

    menuItems = allItems.filter((item) => {
      if (!item.pageKey) return true;
      return hasAccess(item.pageKey);
    });
  }

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[55] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar. z-[56] keeps the drawer and its overlay above the fixed
          BottomNavigation (z-50) but below modals (z-[60]). */}
      <div className={`
        fixed top-0 left-0 h-full w-72 max-w-[85vw] sm:w-64 bg-white dark:bg-gray-800 shadow-lg z-[56] transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-auto lg:w-64 lg:max-w-none
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 shrink-0 rounded-lg overflow-hidden flex items-center justify-center">
                <img
                  src="/ChatGPT Image Oct 8, 2025, 03_35_44 AM.png"
                  alt="GetInv Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                  GetInv
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  Management System
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 p-3 space-y-1 overflow-y-auto overscroll-contain">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`
                    flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors duration-200
                    ${isActive
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="font-medium flex-1 truncate">{item.label}</span>
                  {(item as any).badge > 0 && (
                    <span className="ml-auto min-w-[20px] h-5 flex items-center justify-center px-1.5 rounded-full text-xs font-bold bg-amber-500 text-white">
                      {(item as any).badge > 99 ? '99+' : (item as any).badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 shrink-0 safe-bottom">
            <div className="mb-2 p-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 shrink-0 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {user?.username || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.role || 'Staff'}
                  </p>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-3 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-medium">{t('auth.logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};